import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

export interface TabItem<T extends string = string> {
  label: string;
  value: T;
}

export interface UseTabNavigationOptions<T extends string> {
  tabEnum: Record<string, T | string>;
  defaultTab: T;
  enabled?: boolean;
  labels?: Record<T | string, string>;
}

export interface UseTabNavigationResult<T extends string> {
  activeTab: T;
  handleTabChange: (_event: React.SyntheticEvent, newValue: string) => void;
  tabItems: TabItem<T>[];
}

export const useTabNavigation = <T extends string>({
  tabEnum,
  defaultTab,
  enabled = true,
  labels,
}: UseTabNavigationOptions<T>): UseTabNavigationResult<T> => {
  const { tab: tabParam } = useParams<{ tab: string }>();
  const navigate = useNavigate();

  // Generate tab items from enum
  const tabItems = useMemo(() => {
    const items: TabItem<T>[] = [];

    for (const [key, value] of Object.entries(tabEnum)) {
      if (typeof value === "string") {
        const label = labels?.[value] ?? getTabNameFromEnum(key);
        items.push({ label, value: value as T });
      }
    }

    return items;
  }, [tabEnum, labels]);

  const activeTab = useMemo(() => {
    if (!enabled) {
      return defaultTab;
    }

    if (tabParam) {
      const validValues = Object.values(tabEnum) as T[];
      if (validValues.includes(tabParam as T)) {
        return tabParam as T;
      }
    }

    return defaultTab;
  }, [tabParam, defaultTab, enabled, tabEnum]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (!tabParam) {
      void navigate(defaultTab.toString(), { replace: true });
    }
  }, [tabParam, navigate, defaultTab, enabled]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    if (!enabled) {
      return;
    }
    void navigate(`../${newValue}`, { relative: "path" });
  };

  return {
    activeTab,
    handleTabChange,
    tabItems,
  };
};

const getTabNameFromEnum = (value: string): string => {
  // Convert enum key to readable label (e.g., "OVERVIEW" -> "Overview")
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};
